import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  FunnelIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { recordsService } from '../../services/recordsService';
import type { TreatmentCategory, NGAPAct } from '../../types/records';
import { DENTAL_STATES_CONFIG } from '../../types/records';

// ── Target zone keys (mapped to i18n at render time) ──
const TARGET_ZONE_KEYS = [
  { value: '', i18nKey: 'na' },
  { value: 'WHOLE_TOOTH', i18nKey: 'wholeTooth' },
  { value: 'CROWN_SURFACE', i18nKey: 'crownSurface' },
  { value: 'CROWN_WHOLE', i18nKey: 'crownWhole' },
  { value: 'ROOT', i18nKey: 'root' },
  { value: 'NECK', i18nKey: 'neck' },
  { value: 'QUADRANT', i18nKey: 'quadrant' },
  { value: 'ARCADE', i18nKey: 'arcade' },
  { value: 'MOUTH', i18nKey: 'mouth' },
];

const TARGET_ZONE_SHORT_KEYS: Record<string, string> = {
  '': 'na',
  WHOLE_TOOTH: 'wholeTooth',
  CROWN_SURFACE: 'crownSurface',
  CROWN_WHOLE: 'crownWhole',
  ROOT: 'root',
  NECK: 'neck',
  QUADRANT: 'quadrant',
  ARCADE: 'arcade',
  MOUTH: 'mouth',
};

// ── Chart state options (from DENTAL_STATES_CONFIG) ──
const CHART_STATE_RAW_OPTIONS = [
  { value: '', labelKey: '' },
  ...Object.values(DENTAL_STATES_CONFIG).map(s => ({
    value: s.code,
    labelKey: `${s.icon || ''} ${s.code} — ${s.label_fr || s.label}`,
  })),
];

// ── Form data interface ──
interface ActFormData {
  code: string;
  label: string;
  label_ar: string;
  label_en: string;
  category_id: string;
  coefficient: string;
  default_price: string;
  chart_state_code: string;
  target_zone: string;
  duration_minutes: string;
  requires_tooth: boolean;
  is_active: boolean;
  sort_order: string;
}

const EMPTY_FORM: ActFormData = {
  code: '',
  label: '',
  label_ar: '',
  label_en: '',
  category_id: '',
  coefficient: '',
  default_price: '',
  chart_state_code: '',
  target_zone: '',
  duration_minutes: '',
  requires_tooth: true,
  is_active: true,
  sort_order: '0',
};

// ── Category form ──
interface CategoryFormData {
  name: string;
  name_ar: string;
  name_en: string;
  code: string;
  color: string;
  icon: string;
  icon_image: string | null;
  sort_order: string;
  is_active: boolean;
}

const EMPTY_CAT_FORM: CategoryFormData = {
  name: '',
  name_ar: '',
  name_en: '',
  code: '',
  color: '#6366f1',
  icon: '',
  icon_image: null,
  sort_order: '0',
  is_active: true,
};

type ModalMode = 'create-act' | 'edit-act' | 'create-category' | 'edit-category' | null;
type FilterStatus = 'all' | 'active' | 'inactive';

function ActsManagement() {
  const { t } = useTranslation('records');

  // Derived i18n lookups
  const TARGET_ZONE_OPTIONS = useMemo(() => TARGET_ZONE_KEYS.map(tz => ({
    value: tz.value,
    label: t(`settings.targetZones.${tz.i18nKey}`),
  })), [t]);

  const targetZoneShort = useCallback((zone: string) => {
    const key = TARGET_ZONE_SHORT_KEYS[zone] || 'na';
    return t(`settings.targetZonesShort.${key}`);
  }, [t]);

  const CHART_STATE_OPTIONS = useMemo(() => CHART_STATE_RAW_OPTIONS.map(o => ({
    value: o.value,
    label: o.value === '' ? t('settings.acts.form.chartStatePlaceholder') : o.labelKey,
  })), [t]);

  // ── Data state ──
  const [acts, setActs] = useState<NGAPAct[]>([]);
  const [categories, setCategories] = useState<TreatmentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── UI state ──
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // ── Modal state ──
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingActId, setEditingActId] = useState<string | null>(null);
  const [actForm, setActForm] = useState<ActFormData>(EMPTY_FORM);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [catForm, setCatForm] = useState<CategoryFormData>(EMPTY_CAT_FORM);
  const [catIconFile, setCatIconFile] = useState<File | null>(null);
  const [actIconFile, setActIconFile] = useState<File | null>(null);
  const [actIconPreview, setActIconPreview] = useState<string | null>(null);

  // ── Load data ──
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [actsData, catsData] = await Promise.all([
        recordsService.getSettingsActs(true),
        recordsService.getSettingsCategories(),
      ]);
      setActs(actsData);
      setCategories(catsData);
      // Expand all categories by default
      setExpandedCategories(new Set(catsData.map(c => c.id)));
    } catch (e) {
      setError(t('settings.acts.messages.errorLoad'));
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Auto-dismiss messages ──
  useEffect(() => {
    if (successMessage) {
      const tt = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(tt);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const tt = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(tt);
    }
  }, [error]);

  // ── Filter logic ──
  const filteredActs = useMemo(() => {
    let result = acts;
    if (filterStatus === 'active') result = result.filter(a => a.is_active);
    if (filterStatus === 'inactive') result = result.filter(a => !a.is_active);
    if (filterCategory) result = result.filter(a => a.category_id === filterCategory);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(a =>
        a.code.toLowerCase().includes(q) ||
        a.label.toLowerCase().includes(q) ||
        (a.label_en && a.label_en.toLowerCase().includes(q)) ||
        a.category_name.toLowerCase().includes(q)
      );
    }
    return result;
  }, [acts, filterStatus, filterCategory, searchTerm]);

  // ── Group by category ──
  const groupedActs = useMemo(() => {
    const groups: Record<string, { category: TreatmentCategory; acts: NGAPAct[] }> = {};
    // Initialize groups from categories
    for (const cat of categories) {
      groups[cat.id] = { category: cat, acts: [] };
    }
    // Add uncategorized group
    groups['_uncategorized'] = {
      category: { id: '_uncategorized', name: t('settings.acts.table.uncategorized'), name_ar: '', name_en: '', code: '', color: '#94a3b8', icon: '', icon_image: null, sort_order: 999, is_active: true } as TreatmentCategory,
      acts: [],
    };
    // Distribute acts
    for (const act of filteredActs) {
      const groupId = act.category_id || '_uncategorized';
      if (groups[groupId]) {
        groups[groupId].acts.push(act);
      } else {
        groups['_uncategorized'].acts.push(act);
      }
    }
    // Return as sorted array, filter out empty groups
    return Object.values(groups)
      .filter(g => g.acts.length > 0)
      .sort((a, b) => a.category.sort_order - b.category.sort_order);
  }, [filteredActs, categories]);

  // ── Category toggle ──
  const toggleCategoryExpand = useCallback((catId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }, []);

  // ── Act CRUD handlers ──
  const handleCreateAct = useCallback(() => {
    setActForm(EMPTY_FORM);
    setEditingActId(null);
    setActIconFile(null);
    setActIconPreview(null);
    setModalMode('create-act');
  }, []);

  const handleEditAct = useCallback((act: NGAPAct) => {
    setActForm({
      code: act.code,
      label: act.label,
      label_ar: act.label_ar || '',
      label_en: act.label_en || '',
      category_id: act.category_id || '',
      coefficient: act.coefficient?.toString() || '',
      default_price: act.default_price?.toString() || '',
      chart_state_code: act.chart_state_code || '',
      target_zone: act.target_zone || '',
      duration_minutes: act.duration_minutes?.toString() || '',
      requires_tooth: act.requires_tooth,
      is_active: act.is_active,
      sort_order: (act as any).sort_order?.toString() || '0',
    });
    setEditingActId(act.id);
    setActIconFile(null);
    setActIconPreview(act.icon_image || null);
    setModalMode('edit-act');
  }, []);

  const handleSubmitAct = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actForm.label.trim()) {
      setError(t('settings.acts.messages.labelRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: actForm.code.trim(),
        label: actForm.label.trim(),
        label_ar: actForm.label_ar.trim(),
        label_en: actForm.label_en.trim(),
        category_id: actForm.category_id || undefined,
        coefficient: actForm.coefficient ? parseFloat(actForm.coefficient) : null,
        default_price: actForm.default_price ? parseFloat(actForm.default_price) : null,
        chart_state_code: actForm.chart_state_code,
        target_zone: actForm.target_zone,
        duration_minutes: actForm.duration_minutes ? parseInt(actForm.duration_minutes) : null,
        requires_tooth: actForm.requires_tooth,
        is_active: actForm.is_active,
        sort_order: actForm.sort_order ? parseInt(actForm.sort_order) : 0,
      };

      if (modalMode === 'edit-act' && editingActId) {
        await recordsService.updateAct(editingActId, payload);
        if (actIconFile) {
          await recordsService.uploadActIcon(editingActId, actIconFile);
        }
        setSuccessMessage(t('settings.acts.messages.updated'));
      } else {
        const created = await recordsService.createAct(payload);
        if (actIconFile) {
          await recordsService.uploadActIcon(created.id, actIconFile);
        }
        setSuccessMessage(t('settings.acts.messages.created'));
      }

      setModalMode(null);
      await loadData();
    } catch (e: any) {
      setError(e.message || t('settings.acts.messages.errorSave'));
    } finally {
      setSaving(false);
    }
  }, [actForm, modalMode, editingActId, loadData, t]);

  const handleToggleAct = useCallback(async (act: NGAPAct) => {
    try {
      await recordsService.toggleAct(act.id);
      setSuccessMessage(act.is_active ? t('settings.acts.messages.deactivated') : t('settings.acts.messages.activated'));
      await loadData();
    } catch (e: any) {
      setError(e.message || t('settings.acts.messages.errorSave'));
    }
  }, [loadData, t]);

  const handleDeleteAct = useCallback(async (act: NGAPAct) => {
    if (!confirm(t('settings.acts.messages.confirmDelete'))) {
      return;
    }
    try {
      await recordsService.deleteAct(act.id);
      setSuccessMessage(t('settings.acts.messages.deleted'));
      await loadData();
    } catch (e: any) {
      setError(e.message || t('settings.acts.messages.errorSave'));
    }
  }, [loadData, t]);

  // ── Category CRUD handlers ──
  const handleCreateCategory = useCallback(() => {
    setCatForm(EMPTY_CAT_FORM);
    setCatIconFile(null);
    setEditingCategoryId(null);
    setModalMode('create-category');
  }, []);

  const handleEditCategory = useCallback((cat: TreatmentCategory) => {
    setCatForm({
      name: cat.name,
      name_ar: cat.name_ar || '',
      name_en: cat.name_en || '',
      code: cat.code || '',
      color: cat.color || '#6366f1',
      icon: cat.icon || '',
      icon_image: cat.icon_image || null,
      sort_order: cat.sort_order?.toString() || '0',
      is_active: cat.is_active,
    });
    setCatIconFile(null);
    setEditingCategoryId(cat.id);
    setModalMode('edit-category');
  }, []);

  const handleSubmitCategory = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catForm.name.trim()) {
      setError(t('settings.acts.messages.labelRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: catForm.name.trim(),
        name_ar: catForm.name_ar.trim(),
        name_en: catForm.name_en.trim(),
        code: catForm.code.trim(),
        color: catForm.color,
        icon: catForm.icon.trim(),
        sort_order: catForm.sort_order ? parseInt(catForm.sort_order) : 0,
        is_active: catForm.is_active,
      };

      let savedCat: TreatmentCategory;
      if (modalMode === 'edit-category' && editingCategoryId) {
        savedCat = await recordsService.updateCategory(editingCategoryId, payload);
        setSuccessMessage(t('settings.categories.messages.updated'));
      } else {
        savedCat = await recordsService.createCategory(payload);
        setSuccessMessage(t('settings.categories.messages.created'));
      }

      // Upload icon image if a file was selected
      if (catIconFile && savedCat?.id) {
        await recordsService.uploadCategoryIcon(savedCat.id, catIconFile);
      }

      setCatIconFile(null);
      setModalMode(null);
      await loadData();
    } catch (e: any) {
      setError(e.message || t('settings.acts.messages.errorSave'));
    } finally {
      setSaving(false);
    }
  }, [catForm, catIconFile, modalMode, editingCategoryId, loadData, t]);

  const handleDeleteCategory = useCallback(async (cat: TreatmentCategory) => {
    if (!confirm(t('settings.categories.confirmDelete'))) return;
    try {
      await recordsService.deleteCategory(cat.id);
      setSuccessMessage(t('settings.categories.messages.deleted'));
      await loadData();
    } catch (e: any) {
      setError(e.message || t('settings.acts.messages.errorSave'));
    }
  }, [loadData, t]);

  // ── Stats ──
  const stats = useMemo(() => ({
    total: acts.length,
    active: acts.filter(a => a.is_active).length,
    inactive: acts.filter(a => !a.is_active).length,
    categories: categories.length,
    withNGAP: acts.filter(a => a.code && a.code !== 'N/A').length,
    requiresTooth: acts.filter(a => a.requires_tooth).length,
  }), [acts, categories]);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('settings.acts.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Toast Messages ── */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg bg-green-600 text-white shadow-lg animate-fade-in">
          <CheckIcon className="h-5 w-5 shrink-0" />
          <span className="text-sm font-medium">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-2 hover:opacity-80">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-600 text-white shadow-lg animate-fade-in">
          <XMarkIcon className="h-5 w-5 shrink-0" />
          <span className="text-sm font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Stats Bar ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: t('settings.acts.totalActs', { count: stats.total }), value: stats.total, color: 'text-primary' },
          { label: t('settings.acts.showActive'), value: stats.active, color: 'text-green-500' },
          { label: t('settings.acts.showInactive'), value: stats.inactive, color: 'text-red-400' },
          { label: t('settings.acts.manageCategories'), value: stats.categories, color: 'text-indigo-400' },
          { label: t('settings.acts.withNGAP'), value: stats.withNGAP, color: 'text-cyan-400' },
          { label: t('settings.acts.toothSpecific'), value: stats.requiresTooth, color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-card rounded-xl border border-border p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('settings.acts.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Category filter */}
            <div className="flex items-center gap-1">
              <FunnelIcon className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="border border-border rounded-lg bg-background text-foreground text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">{t('settings.acts.allCategories')}</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              {(['all', 'active', 'inactive'] as FilterStatus[]).map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 text-xs font-medium capitalize transition-colors ${
                    filterStatus === s
                      ? 'bg-primary text-white'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {s === 'all' ? t('settings.acts.showAll') : s === 'active' ? t('settings.acts.showActive') : t('settings.acts.showInactive')}
                </button>
              ))}
            </div>

            {/* Results count */}
            <span className="text-xs text-muted-foreground">
              {t('settings.acts.actCount', { count: filteredActs.length })}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 lg:ml-auto">
            <button
              onClick={() => setShowCategoryManager(!showCategoryManager)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {t('settings.acts.manageCategories')}
            </button>
            <button
              onClick={loadData}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {t('settings.acts.refresh')}
            </button>
            <button
              onClick={handleCreateAct}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('settings.acts.createAct')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Category Manager (collapsible) ── */}
      {showCategoryManager && (
        <div className="bg-card rounded-xl border border-border p-5 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-card-foreground">{t('settings.categories.title')}</h3>
            <button onClick={handleCreateCategory} className="btn-primary text-sm">
              <PlusIcon className="h-4 w-4" />
              <span>{t('settings.categories.createCategory')}</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:border-primary/30 transition-colors group">
                {cat.icon_image ? (
                  <img src={cat.icon_image} alt="" className="w-5 h-5 rounded object-contain shrink-0" />
                ) : (
                  <div
                    className="w-4 h-4 rounded-full shrink-0 border border-border"
                    style={{ backgroundColor: cat.color }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-card-foreground truncate">{cat.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {cat.code || t('settings.categories.noCode')} · {t('settings.categories.actsCount', { count: acts.filter(a => a.category_id === cat.id).length })}
                    {!cat.is_active && <span className="text-red-400 ml-1">{t('settings.categories.inactive')}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEditCategory(cat)}
                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                    title={t('settings.categories.editCategoryTitle')}
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-md transition-colors"
                    title={t('settings.categories.deleteCategoryTitle')}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Acts List (grouped by category) ── */}
      {filteredActs.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <DocumentDuplicateIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-card-foreground mb-2">
            {searchTerm || filterCategory || filterStatus !== 'all' ? t('settings.acts.filterNoResults') : t('settings.acts.noActsYet')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchTerm || filterCategory || filterStatus !== 'all'
              ? t('settings.acts.filterNoResultsDesc')
              : t('settings.acts.noActsYetDesc')}
          </p>
          {!searchTerm && !filterCategory && filterStatus === 'all' && (
            <button onClick={handleCreateAct} className="btn-primary">
              <PlusIcon className="h-4 w-4" />
              <span>{t('settings.acts.createFirstAct')}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedActs.map(({ category: cat, acts: groupActs }) => (
            <div key={cat.id} className="bg-card rounded-xl border border-border overflow-hidden">
              {/* Category header */}
              <button
                onClick={() => toggleCategoryExpand(cat.id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted/50 transition-colors"
              >
                {cat.icon_image ? (
                  <img src={cat.icon_image} alt="" className="w-5 h-5 rounded object-contain shrink-0" />
                ) : (
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                )}
                <h3 className="text-sm font-semibold text-card-foreground">{cat.name}</h3>
                {cat.code && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground">
                    {cat.code}
                  </span>
                )}
                <span className="text-xs text-muted-foreground ml-1">
                  ({t('settings.acts.actCount', { count: groupActs.length })})
                </span>
                <div className="ml-auto">
                  {expandedCategories.has(cat.id) ? (
                    <ChevronUpIcon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Acts table */}
              {expandedCategories.has(cat.id) && (
                <div className="border-t border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.code')}</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.label')}</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.coefficient')}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.price')}</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.target')}</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.tooth')}</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.chart')}</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.status')}</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('settings.acts.table.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {groupActs.map(act => (
                          <tr
                            key={act.id}
                            className={`hover:bg-muted/30 transition-colors ${!act.is_active ? 'opacity-50' : ''}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                {act.icon_image && (
                                  <img src={act.icon_image} alt="" className="w-4 h-4 rounded object-contain shrink-0" />
                                )}
                                <span className="font-mono text-xs font-semibold text-primary">{act.code}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-card-foreground text-sm leading-snug line-clamp-2">{act.label}</p>
                              {act.label_en && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{act.label_en}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-xs text-muted-foreground font-mono">
                                {act.coefficient ? `×${act.coefficient}` : '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-card-foreground">
                                {act.default_price ? `${act.default_price.toFixed(0)} ${t('settings.acts.currency')}` : '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-[10px] px-2 py-1 rounded-full bg-muted text-muted-foreground whitespace-nowrap">
                                {targetZoneShort(act.target_zone || '') || act.target_zone || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {act.requires_tooth ? (
                                <span className="text-emerald-500 text-xs font-medium">{t('settings.acts.table.yes')}</span>
                              ) : (
                                <span className="text-muted-foreground text-xs">{t('settings.acts.table.no')}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {act.chart_state_code ? (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-mono"
                                  style={{
                                    backgroundColor: `${DENTAL_STATES_CONFIG[act.chart_state_code]?.color || '#64748b'}20`,
                                    color: DENTAL_STATES_CONFIG[act.chart_state_code]?.color || '#64748b',
                                  }}
                                >
                                  {DENTAL_STATES_CONFIG[act.chart_state_code]?.icon || ''}
                                  {act.chart_state_code}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleToggleAct(act)}
                                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                                  act.is_active
                                    ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                                    : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                                }`}
                                title={act.is_active ? t('settings.acts.clickToDisable') : t('settings.acts.clickToEnable')}
                              >
                                {act.is_active ? (
                                  <><EyeIcon className="h-3.5 w-3.5" /> {t('settings.acts.table.active')}</>
                                ) : (
                                  <><EyeSlashIcon className="h-3.5 w-3.5" /> {t('settings.acts.table.inactive')}</>
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleEditAct(act)}
                                  className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                                  title={t('settings.acts.editActTitle')}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAct(act)}
                                  className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-md transition-colors"
                                  title={t('settings.acts.deleteActTitle')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ── ACT MODAL ── */}
      {/* ═══════════════════════════════════════════ */}
      {(modalMode === 'create-act' || modalMode === 'edit-act') && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border border-border w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="text-lg font-semibold text-card-foreground">
                {modalMode === 'edit-act' ? t('settings.acts.editTitle') : t('settings.acts.createTitle')}
              </h2>
              <button onClick={() => setModalMode(null)} className="p-2 hover:bg-muted rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitAct} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-5">
                {/* Row 1: Code + Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">
                      {t('settings.acts.form.code')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={actForm.code}
                      onChange={e => setActForm(f => ({ ...f, code: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm font-mono"
                      placeholder={t('settings.acts.form.codePlaceholderFull')}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('settings.acts.form.codeHelpFull')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.category')}</label>
                    <select
                      value={actForm.category_id}
                      onChange={e => setActForm(f => ({ ...f, category_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    >
                      <option value="">{t('settings.acts.form.noCategoryOption')}</option>
                      {categories.filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Label (FR) */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">
                    {t('settings.acts.form.label')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={actForm.label}
                    onChange={e => setActForm(f => ({ ...f, label: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                    placeholder={t('settings.acts.form.labelFrPlaceholder')}
                  />
                </div>

                {/* Row 3: Label AR + EN */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.labelAr')}</label>
                    <input
                      type="text"
                      value={actForm.label_ar}
                      onChange={e => setActForm(f => ({ ...f, label_ar: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      dir="rtl"
                      placeholder="الوصف بالعربية (اختياري)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.labelEn')}</label>
                    <input
                      type="text"
                      value={actForm.label_en}
                      onChange={e => setActForm(f => ({ ...f, label_en: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      placeholder={t('settings.acts.form.labelEnPlaceholder')}
                    />
                  </div>
                </div>

                {/* Separator */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold text-card-foreground mb-3">{t('settings.acts.form.pricingSection')}</h4>
                </div>

                {/* Row 4: Coefficient + Default Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.coefficient')}</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={actForm.coefficient}
                      onChange={e => setActForm(f => ({ ...f, coefficient: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      placeholder={t('settings.acts.form.coefficientPlaceholderFull')}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('settings.acts.form.coefficientHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.defaultPriceDH')}</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={actForm.default_price}
                      onChange={e => setActForm(f => ({ ...f, default_price: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      placeholder={t('settings.acts.form.pricePlaceholderFull')}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('settings.acts.form.priceHelp')}</p>
                  </div>
                </div>

                {/* Separator */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold text-card-foreground mb-3">{t('settings.acts.form.applicationSection')}</h4>
                </div>

                {/* Row 5: Target Zone + Requires Tooth */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.applicationTarget')}</label>
                    <select
                      value={actForm.target_zone}
                      onChange={e => setActForm(f => ({ ...f, target_zone: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    >
                      {TARGET_ZONE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('settings.acts.form.targetZoneHelp')}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.requiresToothSelection')}</label>
                      <div className="flex items-center gap-3 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="requires_tooth"
                            checked={actForm.requires_tooth}
                            onChange={() => setActForm(f => ({ ...f, requires_tooth: true }))}
                            className="h-4 w-4 text-primary border-border focus:ring-primary/30"
                          />
                          <span className="text-sm text-card-foreground">{t('settings.acts.form.requiresToothYes')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="requires_tooth"
                            checked={!actForm.requires_tooth}
                            onChange={() => setActForm(f => ({ ...f, requires_tooth: false }))}
                            className="h-4 w-4 text-primary border-border focus:ring-primary/30"
                          />
                          <span className="text-sm text-card-foreground">{t('settings.acts.form.requiresToothNo')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Chart State + Duration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.chartVisualization')}</label>
                    <select
                      value={actForm.chart_state_code}
                      onChange={e => setActForm(f => ({ ...f, chart_state_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    >
                      {CHART_STATE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">{t('settings.acts.form.chartStateHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.defaultDuration')}</label>
                    <input
                      type="number"
                      min="0"
                      value={actForm.duration_minutes}
                      onChange={e => setActForm(f => ({ ...f, duration_minutes: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      placeholder={t('settings.acts.form.durationPlaceholderFull')}
                    />
                  </div>
                </div>

                {/* Row 7: Custom Icon */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">Icône personnalisée</label>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/50 overflow-hidden shrink-0">
                      {actIconFile ? (
                        <img src={URL.createObjectURL(actIconFile)} alt="" className="w-full h-full object-contain" />
                      ) : actIconPreview ? (
                        <img src={actIconPreview} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors cursor-pointer">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                          Choisir une image
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={e => {
                              const f = e.target.files?.[0];
                              if (f) { setActIconFile(f); setActIconPreview(null); }
                            }}
                          />
                        </label>
                        {(actIconFile || actIconPreview) && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!actIconFile && actIconPreview && editingActId) {
                                await recordsService.deleteActIcon(editingActId);
                              }
                              setActIconFile(null);
                              setActIconPreview(null);
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Supprimer l'icône"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground">PNG, JPG, SVG, WebP — max 128×128px, 200 Ko</span>
                    </div>
                  </div>
                </div>

                {/* Row 8: Sort Order + Active toggle */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.sortOrder')}</label>
                    <input
                      type="number"
                      min="0"
                      value={actForm.sort_order}
                      onChange={e => setActForm(f => ({ ...f, sort_order: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.status')}</label>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setActForm(f => ({ ...f, is_active: !f.is_active }))}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                          actForm.is_active ? 'bg-green-500' : 'bg-muted'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ease-in-out ${
                          actForm.is_active ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                      <span className="text-sm text-card-foreground">
                        {actForm.is_active ? t('settings.acts.form.activeDesc') : t('settings.acts.form.inactiveDesc')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors"
                >
                  {t('settings.acts.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <CheckIcon className="h-4 w-4" />
                  )}
                  <span>{modalMode === 'edit-act' ? t('settings.acts.form.update') : t('settings.acts.form.create')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ── CATEGORY MODAL ── */}
      {/* ═══════════════════════════════════════════ */}
      {(modalMode === 'create-category' || modalMode === 'edit-category') && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="text-lg font-semibold text-card-foreground">
                {modalMode === 'edit-category' ? t('settings.categories.editTitle') : t('settings.categories.createTitle')}
              </h2>
              <button onClick={() => setModalMode(null)} className="p-2 hover:bg-muted rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitCategory} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">
                    {t('settings.categories.form.categoryName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={catForm.name}
                    onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                    placeholder={t('settings.categories.form.categoryNamePlaceholder')}
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.categories.form.shortCode')}</label>
                  <input
                    type="text"
                    value={catForm.code}
                    onChange={e => setCatForm(f => ({ ...f, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm font-mono"
                    placeholder={t('settings.categories.form.shortCodePlaceholder')}
                  />
                </div>

                {/* Name AR + EN */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.categories.form.nameArabic')}</label>
                    <input
                      type="text"
                      value={catForm.name_ar}
                      onChange={e => setCatForm(f => ({ ...f, name_ar: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.categories.form.nameEnglish')}</label>
                    <input
                      type="text"
                      value={catForm.name_en}
                      onChange={e => setCatForm(f => ({ ...f, name_en: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                    />
                  </div>
                </div>

                {/* Color + Sort */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.categories.form.color')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={catForm.color}
                        onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))}
                        className="w-10 h-10 border border-border rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={catForm.color}
                        onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm font-mono"
                        placeholder="#6366f1"
                      />
                    </div>
                    {/* Quick color picks */}
                    <div className="flex gap-1.5 mt-2">
                      {['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'].map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCatForm(f => ({ ...f, color: c }))}
                          className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                            catForm.color === c ? 'border-card-foreground scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.categories.form.sortOrder')}</label>
                    <input
                      type="number"
                      min="0"
                      value={catForm.sort_order}
                      onChange={e => setCatForm(f => ({ ...f, sort_order: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                    />
                  </div>
                </div>

                {/* Active toggle */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">{t('settings.acts.form.status')}</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCatForm(f => ({ ...f, is_active: !f.is_active }))}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                        catForm.is_active ? 'bg-green-500' : 'bg-muted'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ${
                        catForm.is_active ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                    <span className="text-sm text-card-foreground">
                      {catForm.is_active ? t('settings.categories.active') : t('settings.categories.inactive2')}
                    </span>
                  </div>
                </div>

                {/* Icon Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1.5">Icône personnalisée</label>
                  <div className="flex items-center gap-3">
                    {/* Preview */}
                    <div className="w-14 h-14 rounded-lg border-2 border-dashed border-border/60 bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
                      {catIconFile ? (
                        <img src={URL.createObjectURL(catIconFile)} alt="Preview" className="w-full h-full object-contain" />
                      ) : catForm.icon_image ? (
                        <img src={catForm.icon_image} alt="Icon" className="w-full h-full object-contain" />
                      ) : catForm.icon ? (
                        <span className="text-2xl">{catForm.icon}</span>
                      ) : (
                        <svg className="w-6 h-6 text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted cursor-pointer transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Choisir une image
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 200 * 1024) {
                                setError('Image trop lourde. Max 200 Ko.');
                                return;
                              }
                              setCatIconFile(file);
                            }
                          }}
                        />
                      </label>
                      {(catIconFile || catForm.icon_image) && (
                        <button
                          type="button"
                          onClick={async () => {
                            setCatIconFile(null);
                            if (catForm.icon_image && editingCategoryId) {
                              try {
                                await recordsService.deleteCategoryIcon(editingCategoryId);
                                setCatForm(f => ({ ...f, icon_image: null }));
                                await loadData();
                              } catch (err) {
                                console.error('Delete icon error:', err);
                              }
                            }
                          }}
                          className="text-[11px] text-red-500 hover:text-red-600 font-medium"
                        >
                          Supprimer l'icône
                        </button>
                      )}
                      <p className="text-[10px] text-muted-foreground">
                        PNG, JPG, SVG, WebP — max 128×128px, 200 Ko
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors"
                >
                  {t('settings.acts.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <CheckIcon className="h-4 w-4" />
                  )}
                  <span>{modalMode === 'edit-category' ? t('settings.categories.update') : t('settings.categories.create')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ActsManagement);
